import {
  collapseColumn,
  column,
  ContentHeader,
  emptyCell,
  GridCellContent,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  groupColumns,
  numericColumn,
  Palette,
  simpleGroupHeader,
  simpleHeader,
  Tag,
  Tooltip,
  useGridTableApi,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { formatToPrice, priceCell, tagCell } from "src/components";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { CostOptionGroupOfferingStatus } from "src/generated/graphqlTypes";
import { ReloadCostsBanner, ReloadCostsBannerType } from "./components/ReloadCostsBanner";
import {
  OptionsTab_CostOptionGroupRollupFragment,
  OptionsTab_RollupStatsFragment,
  OptionsTabQuery,
  useOptionsTabQuery,
} from "./OptionsTab.generated";
import { deltaPercent, deltaPercentageCell, deltaPriceCell } from "./utils";

type OptionsTabProps = { productOfferings: NamedFragment[] };

export function OptionsTab(props: OptionsTabProps) {
  const { productOfferings } = props;
  const productOfferingIds = useMemo(() => productOfferings.map((po) => po.id), [productOfferings]);
  const api = useGridTableApi<Row>();
  // One row per option group with its checkout locations under it, across the offerings, so they're all loaded and
  // sorted here
  const query = useOptionsTabQuery({
    variables: { filter: { productOfferings: productOfferingIds } },
    skip: productOfferingIds.isEmpty,
  });
  const columns = useMemo(() => createColumns(productOfferings), [productOfferings]);
  const createRows = useCallback(
    (data: OptionsTabQuery | undefined): GridDataRow<Row>[] => [
      simpleGroupHeader,
      simpleHeader,
      ...groupByGlobalOptionGroup(data?.costOptionGroupSummaries ?? []).map((group) => ({
        kind: "group" as const,
        id: group.globalOptionGroup.id,
        data: group,
        initCollapsed: true,
        // A group at one location is a single row. If there are multiple, each location is a row under it.
        children:
          group.locations.length > 1
            ? group.locations.map((location) => ({
                kind: "location" as const,
                id: `${group.globalOptionGroup.id}_${location.locationName ?? "none"}`,
                data: location,
              }))
            : undefined,
      })),
    ],
    [],
  );
  return (
    <>
      <ReloadCostsBanner
        type={ReloadCostsBannerType.Options}
        productOfferingIds={productOfferingIds}
        onReload={() => query.refetch()}
      />
      <ContentHeader
        title="Options"
        description="Review option costs and how they vary across Product Offerings. Costs recalculate automatically whenever a cost-changing edit is saved."
      />
      <GridTableLayout
        emptyFallback="There aren't any option groups to display."
        // Columns with the same name share one Edit Columns switch
        shareColumnVisibility
        tableProps={{
          api,
          query,
          createRows,
          columns,
          sorting: { on: "client", initial: ["name", "ASC"] },
          as: "virtual",
          // Stripes every other visible group, so hiding All Product Offerings doesn't leave two plain groups side by side
          style: { columnStriping: Palette.Gray100 },
        }}
      />
    </>
  );
}

type Offering = {
  status: CostOptionGroupOfferingStatus;
  costs: OptionsTab_RollupStatsFragment | undefined;
  prices: OptionsTab_RollupStatsFragment | undefined;
};
type Offerings = Partial<Record<string, Offering>>;
type LocationRow = Omit<OptionsTab_CostOptionGroupRollupFragment, "offerings"> & { offerings: Offerings };
type GroupRow = Pick<OptionsTab_CostOptionGroupRollupFragment, "globalOptionGroup" | "optionCode"> & {
  locations: LocationRow[];
  costs: OptionsTab_RollupStatsFragment;
  prices: OptionsTab_RollupStatsFragment;
  offerings: Offerings;
};
type Row =
  | { kind: "header" }
  | { kind: "groupHeader" }
  | { kind: "group"; data: GroupRow }
  | { kind: "location"; data: LocationRow };

/** One row per global option group, adding up its locations, which become the rows under it. */
function groupByGlobalOptionGroup(rollups: OptionsTab_CostOptionGroupRollupFragment[]): GroupRow[] {
  return Object.values(rollups.groupBy((rollup) => rollup.globalOptionGroup.id)).map((groupRollups) => {
    const { globalOptionGroup, optionCode } = groupRollups[0];
    const locations = groupRollups.map(toLocationRow);
    // Each offering's total over the locations it has the group at, else why it has none at any of them
    const offerings = groupRollups[0].offerings.keyBy(
      ({ productOffering }) => productOffering.id,
      ({ productOffering }): Offering => {
        const cells = locations.map((location) => location.offerings[productOffering.id]).compact();
        const costs = cells.map((cell) => cell.costs).compact();
        if (costs.isEmpty) {
          const notOffered = cells.some((cell) => cell.status === CostOptionGroupOfferingStatus.NotOffered);
          return {
            status: notOffered ? CostOptionGroupOfferingStatus.NotOffered : CostOptionGroupOfferingStatus.NotApplicable,
            costs: undefined,
            prices: undefined,
          };
        }
        return {
          status: CostOptionGroupOfferingStatus.Costed,
          costs: sumStats(costs),
          prices: sumStats(cells.map((cell) => cell.prices).compact()),
        };
      },
    );
    const totals = Object.values(offerings);
    return {
      globalOptionGroup,
      optionCode,
      locations,
      costs: calcSpread(totals.map((total) => total.costs).compact()),
      prices: calcSpread(totals.map((total) => total.prices).compact()),
      offerings,
    };
  });
}

/** The location's row, with each offering's summary as stats like the group row's. */
function toLocationRow(rollup: OptionsTab_CostOptionGroupRollupFragment): LocationRow {
  return {
    ...rollup,
    offerings: rollup.offerings.keyBy(
      ({ productOffering }) => productOffering.id,
      ({ status, summary }) => ({
        status,
        costs: summary
          ? {
              minInCents: summary.minCostInCents,
              maxInCents: summary.maxCostInCents,
              averageInCents: summary.averageCostInCents,
              deltaInCents: summary.deltaCostInCents,
              deltaPercentage: summary.deltaCostPercentage,
            }
          : undefined,
        prices: summary
          ? {
              minInCents: summary.minPriceInCents,
              maxInCents: summary.maxPriceInCents,
              averageInCents: summary.averagePriceInCents,
              deltaInCents: summary.deltaPriceInCents,
              deltaPercentage: summary.deltaPricePercentage,
            }
          : undefined,
      }),
    ),
  };
}

/** The stats added up: the totals of their cheapest, priciest and average configurations. */
function sumStats(stats: OptionsTab_RollupStatsFragment[]): OptionsTab_RollupStatsFragment {
  return toStats({
    minInCents: stats.sum((s) => s.minInCents),
    maxInCents: stats.sum((s) => s.maxInCents),
    averageInCents: stats.sum((s) => s.averageInCents),
  });
}

/** The spread between the stats, with the mean of their averages. */
function calcSpread(stats: OptionsTab_RollupStatsFragment[]): OptionsTab_RollupStatsFragment {
  return toStats({
    minInCents: stats.min((s) => s.minInCents),
    maxInCents: stats.max((s) => s.maxInCents),
    averageInCents: Math.round(stats.sum((s) => s.averageInCents) / stats.length),
  });
}

/** The amounts with their spread in dollars and as a percent of the min. */
function toStats(
  amounts: Pick<OptionsTab_RollupStatsFragment, "minInCents" | "maxInCents" | "averageInCents">,
): OptionsTab_RollupStatsFragment {
  const { minInCents, maxInCents } = amounts;
  return { ...amounts, deltaInCents: maxInCents - minInCents, deltaPercentage: deltaPercent(maxInCents, minInCents) };
}

function createColumns(offerings: NamedFragment[]): GridColumn<Row>[] {
  return [
    collapseColumn<Row>({ sticky: "left" }),
    column<Row>({
      id: "name",
      name: "Name",
      header: "Name",
      groupHeader: emptyCell,
      canHide: false,
      group: ({ globalOptionGroup }) => globalOptionGroup.name,
      location: ({ locationName }) => locationName ?? "",
      mw: "240px",
      sticky: "left",
    }),
    column<Row>({
      id: "type",
      name: "Type",
      header: "Type",
      groupHeader: emptyCell,
      canHide: true,
      group: ({ globalOptionGroup }) => tagCell("neutral", globalOptionGroup.type.name),
      location: emptyCell,
      mw: "140px",
    }),
    column<Row>({
      id: "location",
      name: "Location",
      header: "Location",
      groupHeader: emptyCell,
      canHide: false,
      group: ({ locations }) =>
        locations.length > 1 ? `${locations.length} locations` : (locations[0]?.locationName ?? ""),
      location: ({ locationName }) => locationName ?? "",
      mw: "140px",
    }),
    column<Row>({
      id: "code",
      name: "Code",
      header: "Code",
      groupHeader: emptyCell,
      canHide: true,
      group: ({ optionCode }) => optionCode ?? "",
      location: emptyCell,
      mw: "120px",
    }),
    ...groupColumns<Row>({
      id: "allProductOfferings",
      header: "All Product Offerings",
      columns: [
        numericColumn<Row>({
          id: "costRange",
          name: "Cost Range",
          header: "Cost Range",
          groupHeader: emptyCell,
          canHide: true,
          mw: "200px",
          group: ({ costs }) => rangeCell(costs),
          location: ({ costs }) => rangeCell(costs),
        }),
        numericColumn<Row>({
          id: "averageCostInCents",
          name: "Avg Cost",
          header: "Avg Cost",
          groupHeader: emptyCell,
          canHide: true,
          mw: "120px",
          group: ({ costs }) => priceCell({ valueInCents: costs.averageInCents }),
          location: ({ costs }) => priceCell({ valueInCents: costs.averageInCents }),
        }),
        numericColumn<Row>({
          id: "costDeltaInCents",
          name: "Δ Cost $",
          header: "Δ Cost $",
          groupHeader: emptyCell,
          canHide: true,
          initHidden: true,
          mw: "120px",
          group: ({ costs }) => deltaPriceCell(costs.deltaInCents),
          location: ({ costs }) => deltaPriceCell(costs.deltaInCents),
        }),
        numericColumn<Row>({
          id: "costDeltaPercentage",
          name: "Δ Cost %",
          header: "Δ Cost %",
          groupHeader: emptyCell,
          canHide: true,
          initHidden: true,
          mw: "120px",
          group: ({ costs }) => deltaPercentageCell(costs.deltaPercentage),
          location: ({ costs }) => deltaPercentageCell(costs.deltaPercentage),
        }),
        numericColumn<Row>({
          id: "priceRange",
          name: "Price Range",
          header: "Price Range",
          groupHeader: emptyCell,
          canHide: true,
          initHidden: true,
          mw: "200px",
          group: ({ prices }) => rangeCell(prices),
          location: ({ prices }) => rangeCell(prices),
        }),
        numericColumn<Row>({
          id: "averagePriceInCents",
          name: "Avg Price",
          header: "Avg Price",
          groupHeader: emptyCell,
          canHide: true,
          initHidden: true,
          mw: "120px",
          group: ({ prices }) => priceCell({ valueInCents: prices.averageInCents }),
          location: ({ prices }) => priceCell({ valueInCents: prices.averageInCents }),
        }),
        numericColumn<Row>({
          id: "priceDeltaInCents",
          name: "Δ Price $",
          header: "Δ Price $",
          groupHeader: emptyCell,
          canHide: true,
          initHidden: true,
          mw: "120px",
          group: ({ prices }) => deltaPriceCell(prices.deltaInCents),
          location: ({ prices }) => deltaPriceCell(prices.deltaInCents),
        }),
        numericColumn<Row>({
          id: "priceDeltaPercentage",
          name: "Δ Price %",
          header: "Δ Price %",
          groupHeader: emptyCell,
          canHide: true,
          initHidden: true,
          mw: "120px",
          group: ({ prices }) => deltaPercentageCell(prices.deltaPercentage),
          location: ({ prices }) => deltaPercentageCell(prices.deltaPercentage),
        }),
      ],
    }),
    ...offerings.flatMap((po) => groupColumns<Row>({ id: po.id, header: po.name, columns: offeringColumns(po) })),
  ];
}

function offeringColumns(po: NamedFragment): GridColumn<Row>[] {
  return [
    numericColumn<Row>({
      id: po.id,
      name: "Cost",
      header: "Cost",
      groupHeader: emptyCell,
      canHide: false,
      mw: "200px",
      group: (group) => costCell(group.offerings[po.id], po, group.globalOptionGroup.name, group.locations),
      location: (location) => costCell(location.offerings[po.id], po, location.globalOptionGroup.name, [location]),
    }),
    numericColumn<Row>({
      id: `${po.id}_deltaCostInCents`,
      name: "Δ Cost $",
      header: "Δ Cost $",
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      group: ({ offerings }) => deltaPriceCell(offerings[po.id]?.costs?.deltaInCents),
      location: ({ offerings }) => deltaPriceCell(offerings[po.id]?.costs?.deltaInCents),
    }),
    numericColumn<Row>({
      id: `${po.id}_deltaCostPercentage`,
      name: "Δ Cost %",
      header: "Δ Cost %",
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      group: ({ offerings }) => deltaPercentageCell(offerings[po.id]?.costs?.deltaPercentage),
      location: ({ offerings }) => deltaPercentageCell(offerings[po.id]?.costs?.deltaPercentage),
    }),
    numericColumn<Row>({
      id: `${po.id}_price`,
      name: "Price Range",
      header: "Price",
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "200px",
      group: ({ offerings }) => rangeCell(offerings[po.id]?.prices),
      location: ({ offerings }) => rangeCell(offerings[po.id]?.prices),
    }),
    numericColumn<Row>({
      id: `${po.id}_deltaPriceInCents`,
      name: "Δ Price $",
      header: "Δ Price $",
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      group: ({ offerings }) => deltaPriceCell(offerings[po.id]?.prices?.deltaInCents),
      location: ({ offerings }) => deltaPriceCell(offerings[po.id]?.prices?.deltaInCents),
    }),
    numericColumn<Row>({
      id: `${po.id}_deltaPricePercentage`,
      name: "Δ Price %",
      header: "Δ Price %",
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      group: ({ offerings }) => deltaPercentageCell(offerings[po.id]?.prices?.deltaPercentage),
      location: ({ offerings }) => deltaPercentageCell(offerings[po.id]?.prices?.deltaPercentage),
    }),
  ];
}

/**
 * The offering's cost range. N/A when it has none of the row's locations, or a tag when it has them but not the group,
 * each with a sentence on hover.
 */
function costCell(
  offering: Offering | undefined,
  po: NamedFragment,
  group: string,
  locations: Pick<OptionsTab_CostOptionGroupRollupFragment, "locationName">[],
): GridCellContent {
  if (!offering) return emptyCell;
  if (offering.costs) return rangeCell(offering.costs);
  // "location Great Room" for one location, or "any of the 2 locations" for a group at several
  const locationName = locations[0]?.locationName;
  const location =
    locations.length > 1 ? `any of the ${locations.length} locations` : locationName && `location ${locationName}`;
  const notApplicable = offering.status === CostOptionGroupOfferingStatus.NotApplicable;
  const reason = notApplicable
    ? `${po.name} does not include ${location}.`
    : `${po.name} does not offer ${group}${location ? ` in ${location}` : ""}.`;
  return {
    content: () => <Tooltip title={reason}>{notApplicable ? "N/A" : <Tag type="update" text="Not offered" />}</Tooltip>,
    value: notApplicable ? "N/A" : "Not offered",
  };
}

/** "$1.00 - $2.00", or one amount when they match, sorting by the min. */
function rangeCell(stats: OptionsTab_RollupStatsFragment | undefined): GridCellContent {
  if (!stats) return emptyCell;
  const { minInCents, maxInCents } = stats;
  const min = formatToPrice({ valueInCents: minInCents });
  return {
    content: minInCents === maxInCents ? min : `${min} - ${formatToPrice({ valueInCents: maxInCents })}`,
    value: minInCents,
  };
}
